<template>
  <section id="pricing" class="pricing-section">
    <v-container class="section-container">
      <div class="section-header text-center">
        <span class="section-tag">Прозрачные условия</span>
        <h2 class="section-title">Честная стоимость без скрытых платежей</h2>
        <p class="section-description">
          Единый тариф со всем функционалом платформы: приложение гостя,
          приложение ресторана, QR-меню и аналитика OZO.OFFICE включены.
        </p>

        <!-- Переключатель режима: 1 точка / Сеть заведений -->
        <div class="pricing-mode-switch mt-6">
          <button
            class="mode-btn"
            :class="{ active: pricingMode === 'single' }"
            @click="pricingMode = 'single'"
          >
            Одно заведение
          </button>
          <button
            class="mode-btn"
            :class="{ active: pricingMode === 'chain' }"
            @click="pricingMode = 'chain'"
          >
            <span>Сеть заведений (от 3 точек)</span>
            <i class="ri-fire-fill ml-1 text-warning"></i>
          </button>
        </div>
      </div>

      <v-row justify="center" class="mt-8">
        <!-- Левая колонка: Карточка тарифа -->
        <v-col cols="12" lg="6">
          <div class="pricing-card">
            <div class="pricing-header">
              <span class="plan-label">
                {{
                  pricingMode === "single"
                    ? "Тариф «Стандарт»"
                    : "Тариф «Сеть Ресторанов»"
                }}
              </span>
              <div class="price-wrap">
                <span class="price-num">
                  {{ pricingMode === "single" ? "25 000 ₸" : "20 000 ₸" }}
                </span>
                <span class="price-period"
                  >/ месяц за
                  {{
                    pricingMode === "single" ? "одно заведение" : "точку"
                  }}</span
                >
              </div>
              <p class="commission-notice">
                + комиссия <strong>1%</strong> со всех платежей, проходящих
                через приложение
              </p>
            </div>

            <div class="pricing-divider"></div>

            <ul class="pricing-features">
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>
                  Мобильные приложения на iOS и Android под вашим брендом
                </div>
              </li>
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>QR-веб меню и бесконтактная оплата с каждого стола</div>
              </li>
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>RFM-система лояльности, кэшбэк и авто-купоны</div>
              </li>
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>Модуль собственной доставки с зонами на карте</div>
              </li>
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>Интеграция с iiko, r_keeper и Яндекс Доставкой</div>
              </li>
              <li>
                <i class="ri-checkbox-circle-fill check-icon"></i>
                <div>Аналитическая панель OZO.OFFICE и техподдержка 24/7</div>
              </li>
            </ul>

            <div class="pricing-terms-box">
              <div class="terms-title">Условия сотрудничества:</div>
              <p class="terms-text">
                Общая минимальная сумма платежа (подписка + комиссии) составляет
                <strong>не менее 85 000 ₸ в месяц</strong>.
              </p>
              <div v-if="pricingMode === 'chain'" class="network-discount">
                <i class="ri-sparkling-fill mr-1 text-neon"></i>
                Для сетей от 3 заведений действует скидка на абонентскую плату и
                персональный аккаунт-менеджер.
              </div>
            </div>

            <div class="pricing-actions">
              <v-btn
                block
                x-large
                rounded
                elevation="0"
                class="btn-pricing-cta"
                @click="openWhatsAppTariff"
              >
                <i class="ri-whatsapp-line mr-2 whatsapp-btn-icon"></i>
                Подключить через WhatsApp
              </v-btn>
            </div>
          </div>
        </v-col>

        <!-- Правая колонка: Интерактивный калькулятор экономии ресторана (Задача 3.2) -->
        <v-col cols="12" lg="6">
          <div class="roi-calculator-card">
            <div class="calc-header">
              <span class="calc-tag">Интерактивный расчет</span>
              <h3 class="calc-title">Калькулятор окупаемости</h3>
              <p class="calc-desc">
                Узнайте, сколько ваш ресторан сэкономит на комиссиях агрегаторов
                (до 20%) при переводе заказов в собственное приложение OZO.
              </p>
            </div>

            <!-- Слайдер оборота -->
            <div class="calc-control mt-6">
              <div class="control-label">
                <span>Оборот заказов через приложение в месяц:</span>
                <strong class="turnover-val"
                  >{{ formatNumber(turnover) }} ₸</strong
                >
              </div>
              <input
                v-model.number="turnover"
                type="range"
                min="1000000"
                max="25000000"
                step="500000"
                class="custom-range"
              />
              <div class="range-marks">
                <span>1 млн ₸</span>
                <span>10 млн ₸</span>
                <span>25 млн ₸</span>
              </div>
            </div>

            <!-- Сравнение расходов -->
            <div class="calc-comparison mt-6">
              <div class="compare-box aggregator-box">
                <span class="comp-title">Комиссия агрегаторов (до 20%)</span>
                <span class="comp-value text-red"
                  >{{ formatNumber(aggregatorCost) }} ₸</span
                >
                <span class="comp-sub">теряется каждый месяц</span>
              </div>

              <div class="compare-box ozo-box">
                <span class="comp-title">Платеж в OZO.direct (1% + софт)</span>
                <span class="comp-value text-green"
                  >{{ formatNumber(ozoTotalCost) }} ₸</span
                >
                <span class="comp-sub">фиксированно и прозрачно</span>
              </div>
            </div>

            <!-- Итоговая чистая выгода -->
            <div class="net-savings-box mt-6">
              <div class="savings-left">
                <span class="savings-label">Ваша чистая экономия:</span>
                <span class="savings-num"
                  >+{{ formatNumber(netSavings) }} ₸</span
                >
                <span class="savings-period"
                  >в месяц останется в вашем ресторане</span
                >
              </div>
              <div class="savings-badge">
                В год: +{{ formatNumber(netSavings * 12) }} ₸
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
export default {
  name: "PricingSection",
  data() {
    return {
      pricingMode: "single", // 'single' | 'chain'
      turnover: 6000000, // 6 млн ₸ оборот по умолчанию
    };
  },
  computed: {
    baseFee() {
      return this.pricingMode === "single" ? 25000 : 20000;
    },
    ozoCommission() {
      return Math.round(this.turnover * 0.01);
    },
    ozoTotalCost() {
      const sum = this.baseFee + this.ozoCommission;
      return sum < 85000 ? 85000 : sum;
    },
    aggregatorCost() {
      // 20% стандартная средняя комиссия агрегаторов доставки
      return Math.round(this.turnover * 0.2);
    },
    netSavings() {
      return Math.max(0, this.aggregatorCost - this.ozoTotalCost);
    },
  },
  methods: {
    formatNumber(num) {
      return new Intl.NumberFormat("ru-RU").format(num);
    },
    openWhatsAppTariff() {
      const planName =
        this.pricingMode === "single"
          ? "Стандарт (25 000 ₸/мес)"
          : "Сеть ресторанов (от 3 точек)";
      const msg = `Здравствуйте! Хочу подключить ресторан к Ozo.direct по тарифу ${planName}. Подскажите дальнейшие шаги.`;
      const url = `https://api.whatsapp.com/send?phone=77017224666&text=${encodeURIComponent(
        msg
      )}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    scrollToContact() {
      const target = document.getElementById("contact");
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/_variables.scss";

.pricing-section {
  background-color: $color-bg-light;
  padding: $section-py 0;

  @media (max-width: 960px) {
    padding: $section-py-mobile 0;
  }
}

.section-container {
  max-width: $container-max-width;
  margin: 0 auto;
}

.section-tag {
  display: inline-block;
  color: #0b7a3a;
  background: $color-neon-green-soft;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 6px 14px;
  border-radius: $radius-full;
  margin-bottom: 12px;
}

.section-title {
  font-family: $font-family-heading;
  font-size: 38px;
  font-weight: 800;
  color: $color-text-dark;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    font-size: 28px;
  }
}

.section-description {
  font-size: 16px;
  color: $color-text-secondary;
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Mode switcher */
.pricing-mode-switch {
  display: inline-flex;
  background: #e2e8f0;
  padding: 4px;
  border-radius: $radius-full;
  max-width: 100%;

  @media (max-width: 520px) {
    display: flex;
    flex-direction: column;
    border-radius: $radius-md;
    width: 100%;
  }

  .mode-btn {
    border: none;
    background: transparent;
    padding: 10px 24px;
    border-radius: $radius-full;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.25s ease;

    @media (max-width: 520px) {
      border-radius: $radius-sm;
      padding: 10px 14px;
      font-size: 13px;
      text-align: center;
    }

    &.active {
      background: #ffffff;
      color: $color-text-dark;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      font-weight: 700;
    }
  }
}

.pricing-card {
  background: $color-bg-white;
  border-radius: $radius-lg;
  border: 1.5px solid rgba(0, 230, 118, 0.5);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  padding: 40px 36px;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 600px) {
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
}

.pricing-header {
  text-align: center;

  .plan-label {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: #f1f5f9;
    color: $color-text-dark;
    padding: 6px 14px;
    border-radius: $radius-full;
    margin-bottom: 16px;
  }

  .price-wrap {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;

    .price-num {
      font-family: $font-family-heading;
      font-size: 42px;
      font-weight: 900;
      color: $color-text-dark;
      letter-spacing: -0.02em;

      @media (max-width: 480px) {
        font-size: 34px;
      }
    }

    .price-period {
      font-size: 14px;
      color: $color-text-secondary;
    }
  }

  .commission-notice {
    font-size: 15px;
    color: #0b7a3a;
    font-weight: 500;
    margin: 0;
  }
}

.pricing-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 24px 0;
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;
    color: $color-text-dark;

    .check-icon {
      color: #0ebd60;
      font-weight: 800;
      flex-shrink: 0;
    }
  }
}

.pricing-terms-box {
  background: #f8fafc;
  border-radius: $radius-md;
  padding: 18px;
  margin-bottom: 24px;
  border-left: 4px solid #00e676;

  .terms-title {
    font-size: 13px;
    font-weight: 700;
    color: $color-text-dark;
    margin-bottom: 4px;
  }

  .terms-text {
    font-size: 13px;
    color: $color-text-secondary;
    margin-bottom: 4px;
    line-height: 1.5;
  }

  .network-discount {
    font-size: 12px;
    color: #0b7a3a;
    font-weight: 600;
    margin-top: 6px;
  }
}

.btn-pricing-cta {
  background-color: $color-neon-green !important;
  color: $color-graphite !important;
  font-weight: 800;
  font-size: 16px;
  text-transform: none;
  letter-spacing: 0;
  height: 52px !important;
  box-shadow: $shadow-neon !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 230, 118, 0.45) !important;
  }

  .whatsapp-btn-icon {
    font-size: 22px;
    color: $color-graphite;
  }
}

/* ROI Calculator Card */
.roi-calculator-card {
  background: $color-graphite-dark;
  color: #ffffff;
  border-radius: $radius-lg;
  padding: 40px 36px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 600px) {
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
}

.calc-header {
  .calc-tag {
    display: inline-block;
    color: $color-neon-green;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 8px;
  }

  .calc-title {
    font-family: $font-family-heading;
    font-size: 26px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 8px;

    @media (max-width: 480px) {
      font-size: 22px;
    }
  }

  .calc-desc {
    font-size: 14px;
    color: #94a3b8;
    line-height: 1.5;
    margin: 0;
  }
}

.calc-control {
  .control-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #cbd5e1;
    margin-bottom: 12px;

    .turnover-val {
      font-family: $font-family-heading;
      font-size: 18px;
      color: $color-neon-green;
    }
  }

  .custom-range {
    width: 100%;
    accent-color: $color-neon-green;
    cursor: pointer;
    height: 8px;
    border-radius: 4px;
  }

  .range-marks {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #64748b;
    margin-top: 6px;
  }
}

.calc-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  .compare-box {
    padding: 16px;
    border-radius: $radius-md;
    display: flex;
    flex-direction: column;

    .comp-title {
      font-size: 12px;
      color: #94a3b8;
      margin-bottom: 6px;
    }

    .comp-value {
      font-family: $font-family-heading;
      font-size: 22px;
      font-weight: 800;
      margin-bottom: 2px;

      &.text-red {
        color: #f87171;
      }

      &.text-green {
        color: $color-neon-green;
      }
    }

    .comp-sub {
      font-size: 11px;
      color: #64748b;
    }

    &.aggregator-box {
      background: rgba(239, 68, 68, 0.08);
      border: 1px solid rgba(239, 68, 68, 0.2);
    }

    &.ozo-box {
      background: rgba(0, 230, 118, 0.08);
      border: 1px solid rgba(0, 230, 118, 0.2);
    }
  }
}

.net-savings-box {
  background: linear-gradient(
    135deg,
    rgba(0, 230, 118, 0.15) 0%,
    rgba(0, 230, 118, 0.05) 100%
  );
  border: 1.5px solid rgba(0, 230, 118, 0.4);
  border-radius: $radius-md;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .savings-left {
    display: flex;
    flex-direction: column;

    .savings-label {
      font-size: 13px;
      color: #cbd5e1;
      font-weight: 600;
    }

    .savings-num {
      font-family: $font-family-heading;
      font-size: 32px;
      font-weight: 900;
      color: $color-neon-green;
      line-height: 1.1;
      margin: 4px 0;
      word-break: break-word;

      @media (max-width: 480px) {
        font-size: 24px;
      }
    }

    .savings-period {
      font-size: 12px;
      color: #94a3b8;
    }
  }

  .savings-badge {
    background: $color-neon-green;
    color: $color-graphite;
    font-weight: 800;
    font-size: 13px;
    padding: 8px 16px;
    border-radius: $radius-full;

    @media (max-width: 600px) {
      align-self: stretch;
      text-align: center;
    }
  }
}
</style>
